import { useEffect, useState } from 'react';

import { rescaleImage } from '../utils/image';

export const useImageElement = ({
  imageFetchHeaders,
  imageSrc,
  height,
  width,
  imgRef,
}: {
  imageFetchHeaders?: HeadersInit;
  imageSrc: string;
  height: number;
  width: number;
  imgRef: React.RefObject<HTMLImageElement>;
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({
    height: 0,
    width: 0,
  });
  const [scale, setScale] = useState(1);
  useEffect(() => {
    if (imgRef.current) {
      const img = imgRef.current;

      const handleImageLoad = () => {
        const naturalWidth = img.naturalWidth;
        const naturalHeight = img.naturalHeight;

        if (naturalWidth && naturalHeight) {
          setImageDimensions(
            rescaleImage({
              img,
              width,
              height,
              imageNaturalDimensions: {
                width: naturalWidth,
                height: naturalHeight,
              },
            }),
          );
          const widthScale = width / naturalWidth;
          const heightScale = height / naturalHeight;
          setScale(Math.min(widthScale, heightScale));
          setImageLoaded(true);
        }
      };

      if (imageFetchHeaders) {
        const options = {
          headers: imageFetchHeaders,
        };

        fetch(imageSrc, options)
          .then((res) => {
            if (!res.ok) {
              throw new Error(
                `Failed to fetch image: ${res.status} ${res.statusText}`,
              );
            }
            return res.blob();
          })
          .then((blob) => {
            const objectUrl = URL.createObjectURL(blob);
            img.src = objectUrl;
            img.onload = () => {
              URL.revokeObjectURL(objectUrl);
              handleImageLoad();
            };
          })
          .catch((error) => {
            console.error('Error loading image:', error);
            img.src = imageSrc;
          });
      } else {
        img.src = imageSrc;
        img.onload = handleImageLoad;
      }
    }
  }, [imgRef, imageFetchHeaders, imageSrc, width, height]);

  return { imageDimensions, imageLoaded, scale };
};
